import type { CaptionEditMode } from './caption-edit-mode';
import {
  CAPTION_VERBS,
  type CaptionVerb,
  captionPlatformRewriteInstruction,
  captionVerbInstruction,
} from './caption-writing-tools';

export type CaptionQuickAction =
  | { kind: 'verb'; actionId: string; label: string; instruction: string; verb: CaptionVerb }
  | { kind: 'platform-rewrite'; actionId: string; label: string; instruction: string }
  | { kind: 'tone-menu'; actionId: 'tone'; label: string }
  | { kind: 'fan-out'; actionId: 'fan-out'; label: string };

/**
 * Which caption quick actions belong on the CURRENT tab.
 *
 * Scope used to be invisible: the same four pills rendered on every tab while
 * one instruction only ever wrote the tab you were looking at. Rather than
 * explain that with a toggle or a badge, the row relabels itself — on a
 * platform-specific tab the only offer IS platform-specific, so the pill can't
 * be misread as global.
 *
 * `editingShared` (PostBody's `editingSharedDraft`) is the seam rather than a
 * first-tab check: the first tab stops being master the moment it gains its
 * own override, and PostBody already computes that distinction correctly.
 *
 * A null `activePlatform` falls back to the generic set — there is no platform
 * to name, and a pill reading "Rewrite for undefined" is worse than a verb.
 *
 * Task 16: a fifth action, `'fan-out'` ("Rewrite for each platform"), is
 * appended ONLY on the master tab (`editingShared === true`, not the
 * null-activePlatform fallback above — those are different states even
 * though they hit the same branch) when 2+ platforms are attached. It never
 * renders on a platform-specific tab: this is a one-shot distribution
 * action, not a per-platform text edit, so it belongs with the other
 * master-tab actions but is visually grouped apart from them by the caller
 * (a `border-l` separator in PostBody) — "transforms the text" vs.
 * "distributes it".
 *
 * Final-review fix (round 8): the visibility gate now takes two extra
 * signals instead of the raw `platforms` count alone —
 *  - `captionEditMode` (from `captionEditModeForStatus`): fan-out has no
 *    staged Keep/Revert compare like `runCaptionEdit`'s other actions do, so
 *    it is hidden entirely whenever the post's status would otherwise STAGE
 *    an edit (scheduled) rather than apply it instantly. Firing it on a
 *    scheduled post would commit N captions with no undo — the exact
 *    high-stakes case staging exists to prevent.
 *  - `fanOutEligibleCount`: the count of platforms fan-out will ACTUALLY
 *    fire against (the caller's send-eligible set, e.g.
 *    `enabledSendPlatforms.length` — paused platforms excluded), not the raw
 *    `platforms.length`. A 2-platform post with one paused used to show the
 *    button (gated on `platforms.length > 1`) but only ever rewrite one
 *    platform, and the paused platform appeared in none of the fan-out's
 *    succeeded/skipped/failed toast buckets.
 */
export function resolveCaptionQuickActions(args: {
  editingShared: boolean;
  activePlatform: string | null;
  platformLabel: (platformId: string) => string;
  fanOutEligibleCount: number;
  captionEditMode: CaptionEditMode;
}): CaptionQuickAction[] {
  const { editingShared, activePlatform, platformLabel, fanOutEligibleCount, captionEditMode } =
    args;

  if (editingShared || !activePlatform) {
    const actions: CaptionQuickAction[] = [
      ...CAPTION_VERBS.map((v): CaptionQuickAction => ({
        kind: 'verb',
        actionId: `verb:${v.id}`,
        label: v.label,
        instruction: captionVerbInstruction(v.id),
        verb: v.id,
      })),
      { kind: 'tone-menu', actionId: 'tone', label: 'Tone' },
    ];
    if (editingShared && captionEditMode === 'apply' && fanOutEligibleCount > 1) {
      actions.push({
        kind: 'fan-out',
        actionId: 'fan-out',
        label: 'Rewrite for each platform',
      });
    }
    return actions;
  }

  const label = platformLabel(activePlatform);
  return [
    {
      kind: 'platform-rewrite',
      actionId: 'platform-rewrite',
      label: `Rewrite for ${label}`,
      instruction: captionPlatformRewriteInstruction(label),
    },
    { kind: 'tone-menu', actionId: 'tone', label: 'Adjust tone' },
  ];
}
